import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axiosBase, { AxiosResponse } from 'axios';
import { getLsViewPreferences, setLsViewPreferences } from 'helpers/localStorageHelpers';
import GaEventManager from 'services/ga/gaEventManager';

export const axios = axiosBase.create({
  baseURL: process.env.REACT_APP_BUYERS_BE,
  withCredentials: true,
});

const fetchBuyerInfo = createAsyncThunk('buyer/fetchBuyerInfo', async () => {
  const { data }: AxiosResponse<BuyerTypes.GetBuyerInfoResponse> = await axios.get('/getBuyerInfo');

  GaEventManager.buyerId = data.buyerId;

  return { ...data };
});

const fetchBuyerProfileData = createAsyncThunk(
  'buyer/fetchBuyerProfileData',
  async (payload: { buyerId?: number }, { signal }) => {
    const { buyerId } = payload;

    const { data }: AxiosResponse<BuyerTypes.GetBuyerProfileDetailsResponse> = await axios.get(
      '/getBuyerProfileDetails',
      {
        params: { buyerId },
        signal,
      },
    );

    return { ...data };
  },
);

const fetchPersonalizedAuctions = createAsyncThunk('buyer/fetchPersonalizedAuctions', async () => {
  const { data } = await axios.get<BuyerTypes.PersonalizedAuctionsResponse>(
    '/auctionsLists?include=suggested,viewed,watching,trending,experiments,promoted,viewedHamptonLots,watchingHamptonLots',
    {
      baseURL: process.env.REACT_APP_BUYERS_BE,
    },
  );

  return data;
});

const updatePersonalizedAuctions = createAsyncThunk(
  'buyer/updatePersonalizedAuctions',
  async (
    types: Array<'suggested' | 'viewed' | 'watching' | 'trending' | 'experiments'>,
    thunkAPI,
  ) => {
    const { data } = await axios.get<Partial<BuyerTypes.PersonalizedAuctionsResponse>>(
      `/auctionsLists?include=${types.join()}`,
      {
        baseURL: process.env.REACT_APP_BUYERS_BE,
      },
    );

    return data;
  },
);

export const initialState: BuyerTypes.BuyerState = {
  buyerId: null,
  firstName: null,
  lastName: null,
  featureToggles: [],
  viewLevel: null,
  isLoading: false,
  businessLicense: null,
  company: null,
  email: null,
  phone: null,
  taxIdNumber: null,
  viewPreferences: getLsViewPreferences(),
  circaActive: null,
  tendersPermissions: null,
  logoutPresented: false,
  forceRefresh: false,
};

export const buyerSlice = createSlice({
  name: 'buyer',
  initialState,
  reducers: {
    setBuyerViewPreferences: (
      state,
      action: PayloadAction<{
        type: AuctionsTypes.AuctionTypes | MyAuctionsTypes.MyAuctionsTypes;
        view: 'thumbnails' | 'plain';
      }>,
    ) => {
      const { type, view } = action.payload;

      setLsViewPreferences({ [type]: view });

      return {
        ...state,
        viewPreferences: {
          ...state.viewPreferences,
          [type]: view,
        },
      };
    },
    setBuyerLogoutPresented: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        logoutPresented: action.payload,
      };
    },
    setBuyerForceRefresh: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        forceRefresh: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBuyerInfo.pending, (state) => {
      return {
        ...state,
        isLoading: true,
      };
    });
    builder.addCase(fetchBuyerInfo.fulfilled, (state, action) => {
      return {
        ...state,
        isLoading: false,
        ...action.payload,
      };
    });
    builder.addCase(fetchBuyerProfileData.pending, (state) => {
      return {
        ...state,
        isLoading: true,
      };
    });
    builder.addCase(fetchBuyerProfileData.fulfilled, (state, action) => {
      return {
        ...state,
        isLoading: false,
        ...action.payload,
      };
    });
  },
});

export const buyerActions = {
  ...buyerSlice.actions,
  fetchBuyerInfo,
  fetchBuyerProfileData,
  fetchPersonalizedAuctions,
  updatePersonalizedAuctions,
};

export default buyerSlice.reducer;
